"use client";

import { useEffect, useState } from "react";
import { Carousel } from "@mantine/carousel";
import { useParams } from "next/navigation";
import styles from "./ReviewsCarousel.module.scss";
import { useDictionary } from "@/src/dictionaries/dictionary-provider";

const FEATURABLE_ID = process.env
  .NEXT_PUBLIC_FEATURABLE_ID as string | undefined;

type FeaturableReview = {
  id: string;
  platform: string;
  author: {
    name: string;
    avatarUrl: string;
    profileUrl: string | null;
  };
  title: string | null;
  text: string;
  originalText: string;
  languageCode: string;
  rating: {
    value: number;
    max: number;
  };
  publishedAt: string;
  updatedAt: string;
  createdAt: string;
  lastSyncedAt: string;
  metadata: Record<string, unknown>;
  url: string | null;
};

type FeaturableWidgetResponse = {
  success: boolean;
  widget: {
    uuid: string;
    config: {
      layout: string;
      language: string;
      name: string | null;
      color_scheme: "light" | "dark";
      name_display: string;
      title: string;
      show_title: boolean;
      carousel_autoplay: boolean;
      carousel_speed: number;
      max_characters: number;
      date_display: "relative" | "absolute";
      schema: boolean;
      schema_type: string | null;
      schema_sub_type: string | null;
      schema_sub_sub_type: string | null;
      summary: boolean;
      summary_review_button: boolean;
      show_profile_pictures: boolean;
      custom_css: string;
      allowed_domains: string[];
    };
    reviews: FeaturableReview[];
    isExampleReviews: boolean;
    gbpLocationUuid: string;
    gbpLocationSummary: {
      reviewsCount: number;
      rating: number;
      writeAReviewUri: string;
    };
    showBranding: boolean;
  };
};

function renderStars(rating?: number) {
  const value = rating ?? 0;
  const rounded = Math.round(value);

  return (
    <div className={styles.starRow} aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={i <= rounded ? styles.starActive : styles.starInactive}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function getInitial(name: string) {
  const trimmed = name?.trim();
  if (!trimmed) return "?";
  return trimmed.charAt(0).toUpperCase();
}

export default function ReviewsCarousel() {
  const dictionary = useDictionary();
  const reviewsTexts = dictionary.reviews ?? {};

  const params = useParams();
  const currentLang = (params?.lang as string | undefined)?.toLowerCase() ?? "fr";
  const isFrench = currentLang.startsWith("fr");
  const isEnglish = currentLang.startsWith("en");

  const [reviews, setReviews] = useState<FeaturableReview[]>([]);
  const [averageRating, setAverageRating] = useState<number | undefined>();
  const [totalReviewCount, setTotalReviewCount] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [embla, setEmbla] = useState<any | null>(null); // Embla instance

  // helpers pour les textes avec variables
  const formatAverageLabel = (count: number) =>
    (reviewsTexts.averageRatingLabel as string | undefined)?.replace(
      "{{count}}",
      String(count)
    ) ?? `Note moyenne sur ${count} avis`;

  const formatSrRating = (rating: number, max: number) =>
    (reviewsTexts.srRating as string | undefined)
      ?.replace("{{rating}}", String(rating))
      ?.replace("{{max}}", String(max)) ??
    `Note ${rating} sur ${max}`;

  // choisir le texte de l'avis selon la langue de l'URL
  const getReviewText = (review: FeaturableReview) => {
    if (isFrench) {
      return review.originalText || review.text || "";
    }

    if (isEnglish) {
      return review.text || review.originalText || "";
    }

    return review.text || review.originalText || "";
  };

  useEffect(() => {
    if (!FEATURABLE_ID) {
      console.error("Featurable widget ID is not configured.");
      setIsLoading(false);
      return;
    }

    const fetchReviews = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const res = await fetch(
          `https://featurable.com/api/v2/widgets/${FEATURABLE_ID}`
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data: FeaturableWidgetResponse = await res.json();

        if (!data.success || !data.widget) {
          throw new Error("Featurable API returned success = false");
        }

        const { widget } = data;

        let nonEmptyReviews = widget.reviews.filter(
          (r) =>
            (r.text && r.text.trim().length > 0) ||
            (r.originalText && r.originalText.trim().length > 0)
        );

        // Filtrage par langue pour EN
        if (isEnglish) {
          nonEmptyReviews = nonEmptyReviews.filter((r) =>
            r.languageCode?.toLowerCase().startsWith("en")
          );
        }

        setReviews(nonEmptyReviews);
        setAverageRating(widget.gbpLocationSummary?.rating);
        setTotalReviewCount(widget.gbpLocationSummary?.reviewsCount);
      } catch (err) {
        console.error("Error fetching Featurable widget:", err);
        setError(
          (reviewsTexts.error as string) ??
            "Impossible de charger les avis pour le moment."
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnglish]);

  // Autoplay maison : on avance d'un slide toutes les 1 seconde
  useEffect(() => {
    if (!embla || reviews.length <= 1) return;

    const id = setInterval(() => {
      // si on est en fin de liste, embla gère le loop tout seul (loop=true)
      embla.scrollNext();
    }, 4000);

    return () => clearInterval(id);
  }, [embla, reviews.length]);

  if (!FEATURABLE_ID) return null;

  const hasRating = averageRating && totalReviewCount;

  return (
    <section
      className={styles.reviewsSection}
      aria-labelledby="reviews-carousel-title"
    >
      <div className={styles.reviewsHeader}>
        <h2
          id="reviews-carousel-title"
          className={styles.reviewsTitle}
        >
          {(reviewsTexts.title as string) ?? "Ce que disent nos clients"}
        </h2>
        <p className={styles.reviewsSubtitle}>
          {(reviewsTexts.subtitle as string) ??
            "Avis vérifiés directement depuis notre fiche Google Business."}
        </p>
      </div>

      {hasRating && (
        <div className={styles.ratingSummary} aria-label="Note Google globale">
          <div className={styles.ratingMain}>
            <span className={styles.ratingValue}>
              {averageRating!.toFixed(1)}
            </span>
            {renderStars(averageRating)}
          </div>
          <div className={styles.ratingMeta}>
            <span className={styles.ratingMetaStrong}>
              {formatAverageLabel(totalReviewCount!)}
            </span>
            <span className={styles.googleBadge}>
              <span className={styles.googleLogo} aria-hidden="true">
                <span>G</span>
              </span>
              <span className={styles.googleText}>
                {(reviewsTexts.googleBadge as string) ?? "sur Google"}
              </span>
            </span>
          </div>
        </div>
      )}

      <div
        className={styles.reviewsCarouselWrapper}
        role="region"
        aria-roledescription="carousel"
        aria-label={(reviewsTexts.title as string) ?? "Avis clients"}
      >
        {isLoading && (
          <p className={styles.carouselStatus}>
            {(reviewsTexts.loading as string) ?? "Chargement des avis…"}
          </p>
        )}

        {error && !isLoading && (
          <p className={styles.carouselStatus}>{error}</p>
        )}

        {!isLoading && !error && reviews.length === 0 && (
          <p className={styles.carouselStatus}>
            {(reviewsTexts.empty as string) ?? "Aucun avis disponible."}
          </p>
        )}

        {!isLoading && !error && reviews.length > 0 && (
          <Carousel
            withIndicators={false}
            withControls
            slideSize="100%"
            slideGap="md"
            align="start"
            height="auto"
            loop={reviews.length > 1}
            nextControlProps={{
              "aria-label":
                (reviewsTexts.next as string) ?? "Afficher l’avis suivant",
            }}
            previousControlProps={{
              "aria-label":
                (reviewsTexts.previous as string) ??
                "Afficher l’avis précédent",
            }}
            className={styles.mantineCarousel}
            getEmblaApi={setEmbla}
          >
            {reviews.map((review) => (
              <Carousel.Slide key={review.id}>
                <article
                  className={styles.reviewCard}
                  aria-label={`${
                    (reviewsTexts.title as string) ?? "Avis"
                  } – ${review.author.name}`}
                >
                  <header className={styles.reviewHeader}>
                    <div className={styles.reviewAuthor}>
                      <div className={styles.reviewAvatarWrapper}>
                        <div
                          className={styles.reviewAvatarFallback}
                          aria-hidden="true"
                        >
                          {getInitial(review.author.name)}
                        </div>
                        {review.author.avatarUrl && (
                          <img
                            className={styles.reviewAvatarImg}
                            src={review.author.avatarUrl}
                            alt={`Photo de profil de ${review.author.name}`}
                            loading="lazy"
                            onError={(e) => {
                              // si l'image casse, on la cache → on ne voit que le fallback
                              e.currentTarget.style.visibility = "hidden";
                            }}
                          />
                        )}
                      </div>
                      <div>
                        <p className={styles.reviewAuthorName}>
                          {review.author.name}
                        </p>
                        <div className={styles.reviewRatingRow}>
                          {renderStars(review.rating.value)}
                          <span className={styles.reviewRatingValue}>
                            <span className={styles.srOnly}>
                              {formatSrRating(
                                review.rating.value,
                                review.rating.max
                              )}
                            </span>
                            {review.rating.value.toFixed(1)} /{" "}
                            {review.rating.max}
                          </span>
                        </div>
                      </div>
                    </div>
                  </header>

                  <p className={styles.reviewText}>{getReviewText(review)}</p>

                  <footer className={styles.reviewFooter}>
                    <p className={styles.reviewSource}>
                      {(reviewsTexts.sourceLabel as string) ?? "Avis Google"}
                    </p>
                  </footer>
                </article>
              </Carousel.Slide>
            ))}
          </Carousel>
        )}
      </div>
    </section>
  );
}
